import React, { useState, useEffect } from 'react';
import { sources } from '../api';

const SourcesScreen = ({ user }) => {
  const [sourceList, setSourceList] = useState([]);
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const FREE_LIMIT = 3;
  const isPremium = user?.premium;
  const atLimit = !isPremium && sourceList.length >= FREE_LIMIT;

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      const res = await sources.list();
      setSourceList(res.data);
    } catch (err) {
      setError('Failed to load sources.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (atLimit) return;
    setAdding(true);
    setError('');
    setSuccess('');
    try {
      const res = await sources.create({ url, name });
      setSourceList([...sourceList, res.data]);
      setUrl('');
      setName('');
      setSuccess('Source added — ANIMA will include it in your next brief.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add source.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await sources.destroy(id);
      setSourceList(sourceList.filter((s) => s.id !== id));
    } catch (err) {
      setError('Failed to remove source.');
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Sources</h1>
            <p style={styles.subtitle}>
              ANIMA monitors these feeds and synthesizes them into your daily brief.
            </p>
          </div>
          <div style={styles.limitBadge}>
            <span style={{
              ...styles.limitCount,
              color: atLimit ? 'var(--danger)' : 'var(--primary)'
            }}>
              {sourceList.length}
            </span>
            <span style={styles.limitText}>
              {isPremium ? 'unlimited' : `/ ${FREE_LIMIT} free`}
            </span>
          </div>
        </div>

        {/* Premium banner */}
        {!isPremium && (
          <div style={styles.premiumBanner}>
            <div style={styles.premiumLeft}>
              <span style={styles.premiumIcon}>◈</span>
              <div>
                <div style={styles.premiumTitle}>Upgrade to Premium</div>
                <div style={styles.premiumSub}>Unlimited sources + ANIMA cross-source analysis — $4.95/mo</div>
              </div>
            </div>
            <button className="btn btn-primary" style={{ fontSize: 12, padding: '8px 16px' }}>
              Upgrade
            </button>
          </div>
        )}

        {/* Add form */}
        {!atLimit ? (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardTitle}>Add Source</span>
              <span style={styles.cardHint}>RSS, Atom, or JSON feeds</span>
            </div>
            <form onSubmit={handleAdd} style={styles.form}>
              <div style={styles.formRow}>
                <div style={{ flex: 2 }}>
                  <label style={styles.label}>Feed URL</label>
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com/feed.xml"
                    required
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Name (optional)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Hacker News"
                  />
                </div>
              </div>
              {error && <div style={styles.error}>{error}</div>}
              {success && <div style={styles.successMsg}>{success}</div>}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={adding}
                style={{ alignSelf: 'flex-start', padding: '10px 20px' }}
              >
                {adding ? 'Adding...' : '+ Add Source'}
              </button>
            </form>
          </div>
        ) : (
          <div style={styles.limitReached}>
            <span style={styles.limitIcon}>◈</span>
            <div>
              <div style={styles.limitTitle}>Free limit reached</div>
              <div style={styles.limitSub}>Upgrade to add unlimited sources.</div>
            </div>
          </div>
        )}

        {/* Sources list */}
        <div style={styles.listSection}>
          <div style={styles.listHeader}>
            <span style={styles.listTitle}>Your Feeds</span>
            <span style={styles.listCount}>{sourceList.length} sources</span>
          </div>

          {loading ? (
            <div style={styles.skeletonList}>
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton" style={styles.skeletonItem} />
              ))}
            </div>
          ) : sourceList.length === 0 ? (
            <div style={styles.empty}>
              <div style={styles.emptyIcon}>◎</div>
              <div style={styles.emptyTitle}>No sources yet</div>
              <div style={styles.emptySub}>Add your first RSS feed above to get started.</div>
            </div>
          ) : (
            <div style={styles.list}>
              {sourceList.map((source, i) => (
                <div
                  key={source.id}
                  style={{ ...styles.sourceItem, animationDelay: `${i * 0.05}s` }}
                  className="fade-in"
                >
                  <div style={styles.sourceLeft}>
                    <div style={styles.sourceDot} />
                    <div>
                      <div style={styles.sourceName}>
                        {source.name || new URL(source.url).hostname}
                      </div>
                      <div style={styles.sourceUrl}>{source.url}</div>
                    </div>
                  </div>
                  <button
                    className="btn btn-danger"
                    style={{ fontSize: 12, padding: '6px 12px' }}
                    onClick={() => handleDelete(source.id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    padding: '40px 24px',
  },
  container: {
    maxWidth: 800,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: 'var(--text)',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    marginTop: 6,
  },
  limitBadge: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 4,
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '8px 16px',
  },
  limitCount: {
    fontSize: 22,
    fontWeight: 700,
  },
  limitText: {
    fontSize: 12,
    color: 'var(--text-secondary)',
  },
  premiumBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--anima-dim)',
    border: '1px solid rgba(123, 97, 255, 0.25)',
    borderRadius: 12,
    padding: '16px 20px',
  },
  premiumLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  premiumIcon: {
    fontSize: 20,
    color: 'var(--anima)',
  },
  premiumTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--anima)',
  },
  premiumSub: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    marginTop: 2,
  },
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: 24,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--text)',
  },
  cardHint: {
    fontSize: 12,
    color: 'var(--text-secondary)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  formRow: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  error: {
    background: 'rgba(255, 77, 109, 0.1)',
    border: '1px solid rgba(255, 77, 109, 0.3)',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    color: 'var(--danger)',
  },
  successMsg: {
    background: 'rgba(0, 255, 170, 0.08)',
    border: '1px solid rgba(0, 255, 170, 0.25)',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    color: 'var(--success)',
  },
  limitReached: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    background: 'rgba(255, 77, 109, 0.06)',
    border: '1px solid rgba(255, 77, 109, 0.2)',
    borderRadius: 12,
    padding: '16px 20px',
  },
  limitIcon: {
    fontSize: 24,
    color: 'var(--danger)',
  },
  limitTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--danger)',
  },
  limitSub: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    marginTop: 2,
  },
  listSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  listTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  listCount: {
    fontSize: 12,
    color: 'var(--text-dim)',
  },
  skeletonList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  skeletonItem: {
    height: 64,
    borderRadius: 12,
  },
  empty: {
    textAlign: 'center',
    padding: '48px 24px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
  },
  emptyIcon: {
    fontSize: 32,
    color: 'var(--text-dim)',
    marginBottom: 12,
  },
  emptyTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    marginBottom: 6,
  },
  emptySub: {
    fontSize: 13,
    color: 'var(--text-dim)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  sourceItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '14px 18px',
    transition: 'border-color 0.2s',
  },
  sourceLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    overflow: 'hidden',
  },
  sourceDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'var(--primary)',
    boxShadow: '0 0 6px var(--primary)',
    flexShrink: 0,
  },
  sourceName: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text)',
  },
  sourceUrl: {
    fontSize: 11,
    color: 'var(--text-secondary)',
    marginTop: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 400,
  },
};

export default SourcesScreen;
